/**
 *
 * SELECT DROPDOWN
 *
 */

import { Box } from "@chakra-ui/react";
import {
  autoUpdate,
  flip,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useInteractions,
} from "@floating-ui/react";
import { ReactNode, useState } from "react";

export type TSelectDropdownOption = {
  label: string;
  value: string;
};

const SelectDropdownOption = ({
  children,
  disabled = false,
  active = false,
  onClick,
}: {
  children: ReactNode | string;
  disabled?: boolean;
  active?: boolean;
  onClick?: (isOpen?: boolean) => void;
}) => {
  const handleClick = (isOpen: boolean) => {
    if (onClick) {
      onClick(isOpen);
    }
  };
  const style = {
    px: 4,
    py: 2,
  };
  if (disabled) {
    return (
      <Box {...style} bgColor="gray.200" color="gray.400">
        {children}
      </Box>
    );
  }
  if (active) {
    return (
      <Box {...style} bgColor="brand.200" color="brand.400" onClick={() => handleClick(false)}>
        {children}
      </Box>
    );
  }
  return (
    <Box {...style} _hover={{ bgColor: "gray.200" }} onClick={() => handleClick(true)}>
      {children}
    </Box>
  );
};

const SelectDropdown = ({
  children,
  value,
  preview,
  disabled = false,
}: {
  children?: ReactNode;
  value?: TSelectDropdownOption;
  preview?: ReactNode;
  setValue?: (value: TSelectDropdownOption) => void;
  disabled?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { context } = useFloating({
    placement: "bottom",
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      flip({ padding: 10 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            maxHeight: "20rem",
            minWidth: `${rects.reference.width}px`,
            overflow: "auto",
          });
        },
        padding: 10,
      }),
    ],
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([
    focus,
    dismiss,
  ]);

  return (
    <Box
      h="2.5rem"
      w="100%"
      py="8px"
      px="16px"
      bgColor={ disabled ? "gray.200" : "white" }
      border="solid 1px"
      borderColor="gray.200"
      borderRadius="md"
      ref={context.refs.setReference}
      onClick={() => setIsOpen((prev) => !prev)}
      {...getReferenceProps}
    >
      {preview !== undefined && preview}
      {preview === undefined && value && value.label}
      {isOpen && (
        <Box
          ref={context.refs.setFloating}
          style={context.floatingStyles}
          {...getFloatingProps}
          bgColor="white"
          zIndex={1000}
          border="solid 1px"
          borderColor="gray.200"
          boxShadow="md"
        >
          {children}
        </Box>
      )}
    </Box>
  );
};

SelectDropdown.Option = SelectDropdownOption;
export default SelectDropdown;
